// Pure scoring helpers — kept free of cross-cutting imports so they run
// under bare Node for testing (see tests/scoring.test.ts).

export interface ScoringAction {
  id: number;
  category: string;
  points: number;
}

export const CATEGORY_LOCAL = 'Alimentation locale';
export const CATEGORY_BALANCED = 'Alimentation équilibrée';
export const CATEGORY_ZERO_WASTE = 'Zéro déchet';
export const CATEGORY_ORDER = [CATEGORY_LOCAL, CATEGORY_BALANCED, CATEGORY_ZERO_WASTE] as const;
export const LOTO_BONUS_POINTS = 50;

export interface PointsBreakdown {
  actionPoints: number;
  zeroWastePoints: number;
  balancedFoodPoints: number;
  localFoodPoints: number;
  lotoBonus: number;
  totalPoints: number;
}

export function computePoints(actions: ScoringAction[], pendingIds: number[]): {
  actionPoints: number;
  zeroWastePoints: number;
  balancedFoodPoints: number;
  localFoodPoints: number;
} {
  let actionPoints = 0;
  let zeroWastePoints = 0;
  let balancedFoodPoints = 0;
  let localFoodPoints = 0;

  for (const id of pendingIds) {
    const action = actions.find((a) => a.id === id);
    if (!action) continue;
    actionPoints += action.points;
    if (action.category === CATEGORY_ZERO_WASTE) zeroWastePoints += action.points;
    else if (action.category === CATEGORY_BALANCED) balancedFoodPoints += action.points;
    else if (action.category === CATEGORY_LOCAL) localFoodPoints += action.points;
  }

  return { actionPoints, zeroWastePoints, balancedFoodPoints, localFoodPoints };
}

/**
 * +LOTO_BONUS_POINTS per category where all actions are complete AFTER
 * pending validation but were NOT all complete BEFORE — prevents double-award.
 */
export function computeLotoBonus(
  actions: ScoringAction[],
  alreadyDoneIds: number[],
  pendingIds: number[]
): number {
  const completedSet = new Set([...alreadyDoneIds, ...pendingIds]);
  const alreadyDoneSet = new Set(alreadyDoneIds);

  let bonus = 0;
  for (const category of CATEGORY_ORDER) {
    const idsInCat = actions.filter((a) => a.category === category).map((a) => a.id);
    if (idsInCat.length === 0) continue;
    const completeAfter = idsInCat.every((id) => completedSet.has(id));
    const completeBefore = idsInCat.every((id) => alreadyDoneSet.has(id));
    if (completeAfter && !completeBefore) {
      bonus += LOTO_BONUS_POINTS;
    }
  }
  return bonus;
}

export function computeBreakdown(
  actions: ScoringAction[],
  alreadyDoneIds: number[],
  pendingIds: number[]
): PointsBreakdown {
  const points = computePoints(actions, pendingIds);
  const lotoBonus = computeLotoBonus(actions, alreadyDoneIds, pendingIds);
  return {
    ...points,
    lotoBonus,
    totalPoints: points.actionPoints + lotoBonus,
  };
}

// ---------------------------------------------------------------------------
// Streak / objective-met counters
// ---------------------------------------------------------------------------

export interface StreakInput {
  currentStreak: number;
  bestStreak: number;
  daysWithObjectiveMet: number;
  lastObjectiveDate: string | null; // 'YYYY-MM-DD' or null
  today: string;                    // 'YYYY-MM-DD'
  yesterday: string;                // 'YYYY-MM-DD'
  objectiveMet: boolean;
}

export interface StreakUpdate {
  /** True if the caller should write to DB; false if nothing changed. */
  shouldUpdate: boolean;
  streakDays: number;
  bestStreak: number;
  daysWithObjectiveMet: number;
  lastObjectiveDate: string;
}

/**
 * Pure streak computation.
 *
 * Rules (semantics: streak = consecutive days where the daily objective was met):
 *   - If objective NOT met → no update at all (streak doesn't get touched).
 *   - If already counted today (lastObjectiveDate === today) → no update.
 *   - If lastObjectiveDate === yesterday → streak += 1.
 *   - Otherwise (first time ever, or streak broken) → streak = 1.
 *   - daysWithObjectiveMet increments by 1 only when crossing into a new day.
 *   - bestStreak = max(streak, bestStreak).
 */
export function computeStreakUpdate(input: StreakInput): StreakUpdate {
  const {
    currentStreak,
    bestStreak,
    daysWithObjectiveMet,
    lastObjectiveDate,
    today,
    yesterday,
    objectiveMet,
  } = input;

  if (!objectiveMet) {
    return {
      shouldUpdate: false,
      streakDays: currentStreak,
      bestStreak,
      daysWithObjectiveMet,
      lastObjectiveDate: lastObjectiveDate ?? '',
    };
  }

  if (lastObjectiveDate === today) {
    // Already counted today — idempotent.
    return {
      shouldUpdate: false,
      streakDays: currentStreak,
      bestStreak,
      daysWithObjectiveMet,
      lastObjectiveDate: today,
    };
  }

  const nextStreak = lastObjectiveDate === yesterday ? currentStreak + 1 : 1;

  return {
    shouldUpdate: true,
    streakDays: nextStreak,
    bestStreak: Math.max(nextStreak, bestStreak),
    daysWithObjectiveMet: daysWithObjectiveMet + 1,
    lastObjectiveDate: today,
  };
}

/**
 * Whether the user has met today's objective.
 * Combines actions already saved today + actions about to be saved.
 * Guards against a zero-objective edge case (would otherwise be "always met").
 */
export function isObjectiveMet(
  alreadyDoneCount: number,
  newCount: number,
  objectiveCount: number,
): boolean {
  if (objectiveCount <= 0) return false;
  return alreadyDoneCount + newCount >= objectiveCount;
}

// ---------------------------------------------------------------------------
// Medal tier — SINGLE source of truth used everywhere (profil, celebration…)
// ---------------------------------------------------------------------------

export type MedalTier = 'bronze' | 'silver' | 'gold';

/** Thresholds in actions completed *that day* (out of the 9 daily actions). */
export const MEDAL_SILVER_ACTIONS = 4;
export const MEDAL_GOLD_ACTIONS = 7;

/**
 * Returns the medal tier based on the number of actions completed in the
 * day (out of 9). Same formula everywhere so profile, celebration, etc.
 * stay consistent.
 *
 *   bronze : 0-3 actions
 *   silver : 4-6 actions
 *   gold   : 7-9 actions
 */
export function computeMedalTier(dailyActionsCount: number): MedalTier {
  if (dailyActionsCount >= MEDAL_GOLD_ACTIONS) return 'gold';
  if (dailyActionsCount >= MEDAL_SILVER_ACTIONS) return 'silver';
  return 'bronze';
}
